import {AfterViewInit, Component, Inject, OnInit, PLATFORM_ID, TemplateRef, ViewChild} from '@angular/core';
import {DerivativeParent, OrderUpsellService} from '@app/product/services/order-upsell.service';
import {ActivatedRoute, Router} from '@angular/router';
import {LOADING, TransactionList} from '@dcp/configurator';
import {DcpTransactionService} from '@app/services/dcp-transaction.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {isPlatformBrowser} from '@angular/common';
import {CLOSE_EVENT, IFrameMessageService} from '@app/services/iframe/iframe-message.service';
import {DefaultModalService} from '@app/shared/modal/services/default-modal.service';
import {first} from 'rxjs/operators';
import {TranslationService} from '@spartacus/core';

@UntilDestroy()
@Component({
  selector: 'dcp-order-upsell-bundle-cta',
  templateUrl: './order-upsell-bundle-cta.component.html',
  styleUrls: ['./order-upsell-bundle-cta.component.scss'],
})
export class OrderUpsellBundleCtaComponent implements OnInit, AfterViewInit {

  isModalOpened: boolean;
  productName: string;
  parentProductName: string;
  product: string;
  parentProduct: string;
  derivativeParents: DerivativeParent[];
  modalId: string;
  title = 'configurator.requestAdditionalLicenses';
  titleTranslation: string;
  private followActionUrl: string;
  private contractId: string;

  @ViewChild('modalContentTemplate') modalContentTemplate: TemplateRef<any>;

  constructor(
    @Inject(PLATFORM_ID) public platformId: string,
    private orderUpsellService: OrderUpsellService,
    private router: Router,
    private dcpTransactionService: DcpTransactionService,
    private activatedRoute: ActivatedRoute,
    private iFrameMessageService: IFrameMessageService,
    private modalService: DefaultModalService,
    private translationService: TranslationService
  ) {
  }

  ngOnInit(): void {
    this.avoidDuplicateModalDisplay();
    this.getUrlParams();
    this.translationService.translate(this.title)
    .pipe(first(), untilDestroyed(this))
    .subscribe((translation) => {
      this.titleTranslation = translation;
  });
  }

  /**
   * Receive the URL params and put them into component's variables in order to display them in the template.
   */
  getUrlParams(): void {
    this.product = this.orderUpsellService.getProduct();
    this.productName = this.orderUpsellService.getProductName();
    this.followActionUrl = this.orderUpsellService.getFollowActionUrl();
    this.parentProductName = this.orderUpsellService.getParentProductName();
    this.parentProduct = this.orderUpsellService.getParentProduct();
    this.derivativeParents = this.orderUpsellService.getDerivativeParent();
    this.contractId = this.orderUpsellService.getContractId();
  }

  openModal() {
    this.modalId = this.modalService.openModal(this.modalContentTemplate, this.titleTranslation, undefined, {customClasses: ['legacy-configurator']});
    this.modalService.onClose().pipe(first(), untilDestroyed(this)).subscribe(() => this.closeModal());
  }

  closeModal() {
    if (isPlatformBrowser(this.platformId) && this.iFrameMessageService.isIframe()) {
      this.iFrameMessageService.postMessage({eventId: CLOSE_EVENT});
    }
  }

  /**
   * Navigate to the configurator for parent product.
   */
  addParentLicense(): void {
    if (this.followActionUrl && this.followActionUrl !== 'null') {
      this.navigateByUrl(this.followActionUrl)
        .finally(() => setTimeout(() => this.modalService.closeModal(this.modalId), 0));
    }
  }

  /**
   * Navigate to the configurator for derivative product.
   */
  addDerivativeLicense(derivativeParentId: string): void {
    if (this.contractId && this.contractId !== 'null') {
      this.dcpTransactionService.getCtaForContractAndProduct(this.contractId, derivativeParentId)
        .pipe(untilDestroyed(this))
        .subscribe((transactionList: TransactionList) => {
          const followActionUrlString: string = transactionList.referencedDocuments[0].items[0].callToActions[0].targetUrl;
          this.navigateByUrl(followActionUrlString)
            .finally(() => setTimeout(() => this.modalService.closeModal(this.modalId), 0));
        });
    }
  }

  avoidDuplicateModalDisplay(): void {
    this.activatedRoute.queryParams.pipe(untilDestroyed(this)).subscribe((value) => {
      if (value.displayMode === 'modal') {
        this.router.navigate([], {
          queryParams: {
            displayMode: null
          },
          queryParamsHandling: 'merge'
        });
      }
    }).unsubscribe();
  }

  private navigateByUrl(followActionUrlString: string): Promise<boolean> {
    if (typeof followActionUrlString !== 'string') {
      throw new TypeError(`Expected a string for 'followActionUrlString', but got ${typeof followActionUrlString}`);
    }
    return this.router.navigateByUrl(
      followActionUrlString
        .replace('configurator', ' /configurator')
        .split(' ')[1] || ''
    );
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId) && this.iFrameMessageService.isIframe()) {
      this.iFrameMessageService.postMessage({eventId: LOADING});
    }
    setTimeout(() => this.openModal(), 0);
  }
}
